import React, { useEffect, useState, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Assignee {
  user_id: string;
  role: string;
  created_at: string;
}

interface OrgMember {
  user_id: string;
  email: string | null;
  member_role: string;
}

interface Props {
  assessmentId: string;
  portfolioId: string;
}

export function AssessmentAssigneesPanel({ assessmentId, portfolioId }: Props) {
  const { toast } = useToast();
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [memberMap, setMemberMap] = useState<Map<string, OrgMember>>(new Map());
  const [loading, setLoading] = useState(true);
  
  const [selectedRespondent, setSelectedRespondent] = useState<string>("");
  const [selectedReviewer, setSelectedReviewer] = useState<string>("");
  const [assigning, setAssigning] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Check if admin to determine which path to use
      const { data: isAdminRpc } = await (supabase.rpc as any)('current_user_is_admin');
      
      // Fetch assignees - use admin RPC if admin, PM RPC otherwise
      const rpcName = isAdminRpc ? 'admin_list_assessment_assignees' : 'pm_list_assessment_assignees';
      console.log(`[ASSIGNEES] RPC=${rpcName} assessmentId=`, assessmentId);
      const { data: assigneesData, error: assigneesErr } = await (supabase.rpc as any)(
        rpcName,
        { p_assessment_id: assessmentId }
      );
      if (assigneesErr) {
        console.error(`[ASSIGNEES] ${rpcName} failed:`, assigneesErr);
      }
      setAssignees((assigneesData ?? []).map((a: any) => ({
        user_id: a.user_id,
        role: a.role,
        created_at: a.created_at,
      })));

      // Fetch all org members for this portfolio
      const membersRpcName = isAdminRpc ? 'admin_list_org_memberships' : 'list_portfolio_users';
      const membersParams = isAdminRpc ? { p_org_id: portfolioId } : { p_portfolio_id: portfolioId };
      console.log(`[ASSIGNEES] RPC=${membersRpcName} portfolioId=`, portfolioId);
      const { data: membersData, error: membersError } = await (supabase.rpc as any)(
        membersRpcName,
        membersParams
      );
      if (membersError) {
        console.error(`[ASSIGNEES] ${membersRpcName} failed:`, membersError);
      }

      const members = (membersData ?? []).filter((m: any) => m.user_id != null) as OrgMember[];
      setOrgMembers(members);

      // Build map for quick lookup
      const map = new Map<string, OrgMember>();
      members.forEach(m => map.set(m.user_id, m));
      setMemberMap(map);
    } catch (err) {
      console.error("Error fetching assignees:", err);
    } finally {
      setLoading(false);
    }
  }, [assessmentId, portfolioId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const assignedUserIds = new Set(assignees.map(a => a.user_id));

  // Filter candidates: members with matching role who aren't already assigned
  const respondentCandidates = orgMembers.filter(
    m => m.member_role === "respondent" && !assignedUserIds.has(m.user_id)
  );
  const reviewerCandidates = orgMembers.filter(
    m => m.member_role === "reviewer" && !assignedUserIds.has(m.user_id)
  );

  const handleAssign = async (userId: string, role: "respondent" | "reviewer") => {
    if (!userId) return;
    setAssigning(true);
    try {
      const { error } = await (supabase.rpc as any)(
        "pm_assign_user_to_assessment",
        { p_assessment_id: assessmentId, p_user_id: userId, p_member_role: role }
      );

      if (error) throw error;

      toast({ title: "Assigned", description: `${role} assigned successfully.` });
      setSelectedRespondent("");
      setSelectedReviewer("");
      fetchData();
    } catch (err: any) {
      console.error("Assignment error:", err);
      toast({
        variant: "destructive",
        title: "Assignment failed",
        description: err?.message ?? "Please try again.",
      });
    } finally {
      setAssigning(false);
    }
  };

  const respondentAssignees = assignees.filter(a => a.role === "respondent");
  const reviewerAssignees = assignees.filter(a => a.role === "reviewer");

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading assignees...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4" />
          Assessment Assignees
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Respondents Section */}
        <div>
          <h4 className="font-medium text-sm mb-2">Respondents</h4>
          {respondentAssignees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No respondents assigned</p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-2">
              {respondentAssignees.map(a => {
                const member = memberMap.get(a.user_id);
                return (
                  <Badge key={a.user_id} variant="secondary">
                    {member?.email ?? a.user_id.slice(0, 8)}
                  </Badge>
                );
              })}
            </div>
          )}
          
          {respondentCandidates.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <Select value={selectedRespondent} onValueChange={setSelectedRespondent}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select respondent" />
                </SelectTrigger>
                <SelectContent>
                  {respondentCandidates.map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.email ?? m.user_id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => handleAssign(selectedRespondent, "respondent")}
                disabled={!selectedRespondent || assigning}
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Assign
              </Button>
            </div>
          )}
        </div>

        {/* Reviewers Section */}
        <div>
          <h4 className="font-medium text-sm mb-2">Reviewers</h4>
          {reviewerAssignees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reviewers assigned</p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-2">
              {reviewerAssignees.map(a => {
                const member = memberMap.get(a.user_id);
                return (
                  <Badge key={a.user_id} variant="outline">
                    {member?.email ?? a.user_id.slice(0, 8)}
                  </Badge>
                );
              })}
            </div>
          )}
          
          {reviewerCandidates.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <Select value={selectedReviewer} onValueChange={setSelectedReviewer}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select reviewer" />
                </SelectTrigger>
                <SelectContent>
                  {reviewerCandidates.map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.email ?? m.user_id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAssign(selectedReviewer, "reviewer")}
                disabled={!selectedReviewer || assigning}
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Assign
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
